import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { GameState, Position, PieceType, IMove, IPiece } from "..\/..\/..\/..\/types";
import { MCTSUtils_v2 } from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 27: Positional Advancement (Simplified)
 *
 * SIMPLIFIED: Focus only on normal positional advancement and territorial control.
 * King support logic has been moved to Priority 24 (King Backup Support).
 *
 * This priority now handles:
 * - Advancing pieces toward enemy territory
 * - Controlling key squares
 * - Avoiding retreats without good reason
 */
export class PositionalAdvancement extends BaseDecisionPriority {
  readonly priority = 28;
  readonly name = "Positional Advancement";
  readonly description =
    "Advance pieces and control key squares - simplified without king support";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant as the final positional evaluation step
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    const gameState = context.gameState;
    const allMoves = context.allPossibleMoves;

    // Score all moves for positional advancement
    const scoredMoves = this.scoreMovesForPositionalAdvancement(
      gameState,
      allMoves
    );

    // Filter to moves that advance or at least don't retreat
    const advancingMoves = scoredMoves.filter((sm) => sm.score > 0);

    if (advancingMoves.length > 0) {
      const bestMoves = advancingMoves
        .sort((a, b) => b.score - a.score)
        .slice(0, Math.max(3, Math.floor(advancingMoves.length * 0.6)))
        .map((sm) => sm.move);

      this.addLog(
        `🔄 Positional advancement: Found ${bestMoves.length} advancing moves`
      );
      this.addLog(
        `🎯 Strongly favoring forward progress toward enemy territory`
      );

      return this.createFilterResult(
        bestMoves,
        "Positional advancement - favoring forward movement",
        {
          logs: this.analysisLogs,
          movesFiltered: allMoves.length - bestMoves.length,
          avgScore:
            advancingMoves.reduce((sum, sm) => sum + sm.score, 0) /
            advancingMoves.length,
        }
      );
    }

    // If no advancing moves, just apply scoring without filtering
    this.addLog(`🔄 No advancing moves found, applying positional scoring`);
    return this.createNoActionResult("No clear advancement opportunities");
  }

  private scoreMovesForPositionalAdvancement(
    gameState: GameState,
    moves: MoveInfo[]
  ): Array<{ move: MoveInfo; score: number }> {
    return moves.map((move) => ({
      move,
      score: this.calculatePositionalScore(gameState, move),
    }));
  }

  private calculatePositionalScore(
    gameState: GameState,
    move: MoveInfo
  ): number {
    let score = 0;
    const toPosition = move.move.position;
    const fromPosition = move.fromPosition;
    const movingPiece = this.getPieceAt(gameState, fromPosition);

    if (!movingPiece) return 0;

    // 1. ADVANCEMENT BONUS: Heavily reward moving toward enemy territory
    const advancementValue = this.calculateAdvancementValue(
      fromPosition,
      toPosition,
      gameState.isLightTurn
    );
    score += advancementValue * 25; // Strong bias toward advancement

    // 2. RETREAT PENALTY: Heavily penalize moving backward
    if (advancementValue < 0) {
      score += advancementValue * 40; // Double penalty for retreating
    }

    // 3. TERRITORIAL CONTROL: Bonus for controlling key squares
    const territorialValue = this.calculateTerritorialValue(
      gameState,
      toPosition,
      movingPiece
    );
    score += territorialValue * 15;

    // 4. ACTIVITY BONUS: Reward pieces that can attack more squares
    const activityBonus = this.calculateActivityBonus(
      gameState,
      toPosition,
      movingPiece
    );
    score += activityBonus * 8;

    // 5. CENTRALIZATION: Slight bonus for central files
    const centralizationBonus = this.calculateCentralizationBonus(toPosition);
    score += centralizationBonus * 5;

    return score;
  }

  private calculateAdvancementValue(
    fromPosition: Position,
    toPosition: Position,
    isLight: boolean
  ): number {
    // Light advances toward row 7, Dark advances toward row 0
    const targetRow = isLight ? 7 : 0;

    const oldDistance = Math.abs(fromPosition.row - targetRow);
    const newDistance = Math.abs(toPosition.row - targetRow);

    // Positive score if advancing, negative if retreating
    return oldDistance - newDistance;
  }

  private calculateTerritorialValue(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): number {
    const isLight = gameState.isLightTurn;

    // Bonus for advancing into enemy territory
    if (isLight && position.row >= 5) {
      return 3; // Light in enemy territory (rows 5-7)
    } else if (!isLight && position.row <= 2) {
      return 3; // Dark in enemy territory (rows 0-2)
    }

    // Bonus for controlling center
    if (position.row >= 3 && position.row <= 4) {
      return 2; // Center control
    }

    return 0;
  }

  private calculateActivityBonus(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): number {
    // Simplified activity calculation - pieces in advanced positions
    // typically have more attacking potential
    const isLight = gameState.isLightTurn;

    if (isLight && position.row >= 4) {
      return 2; // Light piece actively positioned
    } else if (!isLight && position.row <= 3) {
      return 2; // Dark piece actively positioned
    }

    return 0;
  }

  private calculateCentralizationBonus(position: Position): number {
    // Slight preference for central files (columns 1, 2, 3)
    if (position.col >= 1 && position.col <= 3) {
      return 1;
    }
    return 0;
  }

  // Utility methods
  private getPieceAt(gameState: GameState, position: Position): IPiece | null {
    if (!this.isValidPosition(position)) return null;
    const pieces = gameState.board[position.row][position.col].Pieces;
    return pieces.length > 0 ? pieces[0] : null;
  }

  private isValidPosition(position: Position): boolean {
    return (
      position.row >= 0 &&
      position.row < 8 &&
      position.col >= 0 &&
      position.col < 8
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
}

